/**
 * Plan Actions Hook
 *
 * Centralized hook for plan action handlers (archive, unarchive, activate).
 * Provides memoized action handlers to prevent recreation on every render.
 */

import { ENTITY_STATUS } from '@monorepo/shared/schemas/common'
import { useNavigate } from '@tanstack/react-router'
import { useCallback } from 'react'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import type { Plan } from '../types/plan.types'
import { useUpdatePlanStatus } from './usePlans'

export interface PlanActionHandlers {
  handleEdit: (plan: Plan) => void
  handleArchive: (plan: Plan) => Promise<void>
  handleUnarchive: (plan: Plan) => Promise<void>
  handleActivate: (plan: Plan) => Promise<void>
}

/**
 * Hook that provides memoized plan action handlers
 */
export function usePlanActions(): PlanActionHandlers {
  const navigate = useNavigate()
  const updatePlanStatus = useUpdatePlanStatus()

  const handleEdit = useCallback(
    (plan: Plan) => {
      navigate({ to: '/catalog/plans/$planId', params: { planId: plan.id } })
    },
    [navigate]
  )

  const handleArchive = useCallback(
    async (plan: Plan) => {
      try {
        await updatePlanStatus.mutateAsync({
          id: plan.id,
          status: ENTITY_STATUS.archived,
        })
      } catch (error) {
        toast.error(extractErrorMessage(error))
        throw error
      }
    },
    [updatePlanStatus]
  )

  const handleUnarchive = useCallback(
    async (plan: Plan) => {
      try {
        await updatePlanStatus.mutateAsync({
          id: plan.id,
          status: ENTITY_STATUS.draft,
        })
      } catch (error) {
        toast.error(extractErrorMessage(error))
        throw error
      }
    },
    [updatePlanStatus]
  )

  const handleActivate = useCallback(
    async (plan: Plan) => {
      try {
        await updatePlanStatus.mutateAsync({
          id: plan.id,
          status: ENTITY_STATUS.active,
        })
      } catch (error) {
        toast.error(extractErrorMessage(error))
        throw error
      }
    },
    [updatePlanStatus]
  )

  return {
    handleEdit,
    handleArchive,
    handleUnarchive,
    handleActivate,
  }
}
